import { FaChevronDown, FaDatabase } from "react-icons/fa";
import PropTypes from "prop-types";
import {
  getBadgeProps,
  getCategoryBadgeProps,
  ALTERNATIVE_ANALYSIS_CATEGORY,
  BASELINE_CATEGORY,
} from "../../../utils/common-utils";
import "../Navix.css";

function softwareGroupLabel(softwareName) {
  const value = (softwareName || "").toLowerCase();
  if (value.includes("ras")) return "RAS";
  if (value.includes("hms")) return "HMS";
  return "Other";
}

function groupModels(models, onlyRasHms) {
  const groups = { RAS: [], HMS: [], Other: [] };
  models.forEach((model) => {
    groups[softwareGroupLabel(model.model_software_name)].push(model);
  });
  const rasHms = ["RAS", "HMS"].filter((key) => groups[key].length > 0);
  const order = onlyRasHms
    ? rasHms.length
      ? rasHms
      : ["Other"]
    : ["RAS", "HMS", "Other"];
  return order
    .filter((key) => groups[key].length > 0)
    .map((key) => ({ title: key, models: groups[key] }));
}

/**
 * LayerPanel component for displaying layer models
 */
const LayerPanel = ({
  selectedLayer,
  layerModels,
  loadingModels,
  hasMoreModels,
  loadMoreLayerModels,
  handleOpenPopup,
  setIsPanelVisible,
}) => {
  const getSourceStyle = (source) => {
    const badgeProps = getBadgeProps(source);
    return {
      borderColor: badgeProps.backgroundColor,
      backgroundColor: `${badgeProps.backgroundColor}20`, // 20% opacity
      iconColor: badgeProps.backgroundColor,
      textColor: "#FFFFFF", // Brighter white text
    };
  };

  const renderModelItem = (model, index) => {
    const sourceStyle = getSourceStyle(model.source_application);
    const categoryBadge = getCategoryBadgeProps(model.model_category);
    return (
      <div
        key={`${model.tdis_identifier || model.name}-${index}`}
        className="model-item"
        onClick={() => handleOpenPopup(model)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleOpenPopup(model);
          }
        }}
        role="button"
        tabIndex={0}
        aria-label={`Open details for ${model.name}`}
        style={{
          borderLeft: `4px solid ${sourceStyle.borderColor}`,
        }}
      >
        <div className="model-content">
          <div className="model-header">
            <div
              className="model-name"
              style={{ color: sourceStyle.textColor }}
            >
              {model.name}
            </div>
            {model.source_application && (
              <div
                className="model-source"
                style={{ color: sourceStyle.iconColor }}
              >
                <FaDatabase size={12} />
                <span>&nbsp; {model.source_application.toUpperCase()}</span>
                {categoryBadge && (
                  <span
                    style={{
                      backgroundColor: categoryBadge.bg,
                      color: categoryBadge.textColor || "#fff",
                      marginLeft: "6px",
                      padding: "0px 8px",
                      borderRadius: "12px",
                      fontSize: "0.7rem",
                      fontWeight: "600",
                      display: "inline-block",
                      verticalAlign: "middle",
                      letterSpacing: "0.5px",
                    }}
                  >
                    {categoryBadge.label}
                  </span>
                )}
              </div>
            )}
          </div>
          <div
            className="model-description"
            style={{ color: sourceStyle.textColor }}
          >
            {model.description && model.description.length > 100
              ? `${model.description.substring(0, 100)}...`
              : model.description}
          </div>
        </div>
        <div
          className="model-action-icon"
          style={{ color: sourceStyle.textColor }}
        >
          <FaChevronDown />
        </div>
      </div>
    );
  };

  const hasAlternative = layerModels.some(
    (model) => model.model_category === ALTERNATIVE_ANALYSIS_CATEGORY,
  );
  const hasBaseline = layerModels.some(
    (model) => model.model_category === BASELINE_CATEGORY,
  );
  const onlyRasHms = hasAlternative && !hasBaseline;
  const groupedModels = groupModels(layerModels, onlyRasHms);

  let modelsSectionContent;
  if (loadingModels && layerModels.length === 0) {
    modelsSectionContent = (
      <div className="loading-models">
        <div className="spinner-small"></div>
        <span>Loading Models...</span>
      </div>
    );
  } else if (groupedModels.length > 0) {
    modelsSectionContent = (
      <>
        <div className="models-list">
          {groupedModels.map((group) => (
            <div className="model-software-group" key={group.title}>
              <h4 className="model-software-heading">{group.title}</h4>
              {group.models.map((model, index) =>
                renderModelItem(model, index),
              )}
            </div>
          ))}
        </div>
        {hasMoreModels && (
          <div className="load-more-models">
            <button
              className="load-more-btn"
              onClick={loadMoreLayerModels}
              disabled={loadingModels}
            >
              {loadingModels ? (
                <div className="spinner-small"></div>
              ) : (
                <>
                  Load More <FaChevronDown />
                </>
              )}
            </button>
          </div>
        )}
      </>
    );
  } else {
    modelsSectionContent = (
      <div className="no-models">No models found in this layer.</div>
    );
  }

  return (
    <div className="counties-panel visible">
      <div className="panel-header">
        <h3 title={selectedLayer}>{selectedLayer}</h3>
        <button
          className="close-panel"
          onClick={() => setIsPanelVisible(false)}
          aria-label="Close panel"
        >
          ×
        </button>
      </div>
      <div className="models-section">{modelsSectionContent}</div>
    </div>
  );
};

LayerPanel.propTypes = {
  selectedLayer: PropTypes.string,
  layerModels: PropTypes.array,
  loadingModels: PropTypes.bool,
  hasMoreModels: PropTypes.bool,
  loadMoreLayerModels: PropTypes.func,
  handleOpenPopup: PropTypes.func,
  setIsPanelVisible: PropTypes.func,
};

export default LayerPanel;
